<template>
  <div class="popup-block fadeIn active" id="popup-id" @click="onOverlayClick">
    <div class="popup-block__overlay">
      <div
        ref="dialog"
        :class="['popup', popupClass, `size-${size}`]"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @click.stop=""
      >
        <div
          class="popup__header"
          :class="{ 'popup__header--custom': hasHeaderSlot }"
        >
          <template v-if="hasHeaderSlot">
            <span :id="titleId" class="sr-only">{{ title }}</span>
            <slot name="header" />
          </template>
          <div v-else class="popup__header-inner">
            <div class="popup__header-group">
              <div
                :id="titleId"
                class="popup__header-title"
                :class="titleClass"
              >
                {{ title }}
              </div>
            </div>
            <div class="popup__header-controls">
              <button
                type="button"
                ref="closeBtn"
                class="popup__close-btn"
                aria-label="סגור"
                @click.stop="$emit('close')"
              >
                <icon name="close-modal" />
              </button>
            </div>
          </div>
        </div>
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Components/UI/Icon';

export default {
  components: { Icon },
  props: {
    title: {
      type: String,
      required: true
    },
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['sm', 'md', 'lg', 'xl'].includes(value);
      }
    },
    popupClass: {
      default: ''
    },
    titleClass: {
      default: ''
    },
    confirmOnOverlay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      titleId: `modal-title-${this._uid}`,
      previouslyFocused: null,
      ignoreOverlayUntil: 0
    };
  },
  computed: {
    hasHeaderSlot() {
      return !!(
        (this.$scopedSlots && this.$scopedSlots.header) ||
        (this.$slots && this.$slots.header)
      );
    }
  },
  mounted() {
    this.previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.handleKeydown);
    this.ignoreOverlayUntil = Date.now() + 400;
    this.hideCrisp();
    setTimeout(() => {
      this.focusFirstElement();
    }, 0);
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleKeydown);
    this.restoreCrisp();
    if (
      this.previouslyFocused &&
      typeof this.previouslyFocused.focus === 'function'
    ) {
      this.previouslyFocused.focus();
    }
  },
  methods: {
    hideCrisp() {
      try {
        if (window.$crisp && typeof window.$crisp.push === 'function') {
          window.$crisp.push(['do', 'chat:hide']);
        }
      } catch (e) {
        // Crisp unavailable — ignore.
      }
    },
    restoreCrisp() {
      // On mobile the launcher stays hidden; tab bar opens chat on demand.
      try {
        if (
          window.$crisp &&
          typeof window.$crisp.push === 'function' &&
          window.innerWidth > 760
        ) {
          window.$crisp.push(['do', 'chat:show']);
        }
      } catch (e) {
        // Crisp unavailable — ignore.
      }
    },
    onOverlayClick() {
      if (Date.now() < this.ignoreOverlayUntil) {
        return;
      }
      if (this.confirmOnOverlay) {
        this.$emit('overlay-close');
        return;
      }
      this.$emit('close');
    },
    getFocusableElements() {
      const root = this.$refs.dialog;
      if (!root) {
        return [];
      }
      return Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);
    },
    focusFirstElement() {
      const focusable = this.getFocusableElements();
      if (focusable.length) {
        focusable[0].focus();
      } else if (this.$refs.closeBtn) {
        this.$refs.closeBtn.focus();
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        if (this.confirmOnOverlay) {
          this.$emit('overlay-close');
        } else {
          this.$emit('close');
        }
        return;
      }
      if (e.key !== 'Tab') {
        return;
      }
      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.popup__header--custom {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: transparent !important;
  min-height: 0 !important;
}
.students-color-modal .popup {
  max-width: 260px;
}

.modal-small {
  .popup__header-title {
    font-size: 12px;
    line-height: 16px;
  }

  .popup__header-inner {
    padding: 6px;
  }
}

.text-lrt {
  direction: ltr;
}
.popup-settings {
  max-width: 400px;
  &-inner {
    padding: 16px;
  }
  &-title {
    padding: 16px 0;
  }
}
</style>
